import { useSyncExternalStore } from 'react';
import { useSessionOrNull } from '~/contexts/SessionContext';

/** Stable no-op subscribe used when there is no active session. */
const NOOP_SUBSCRIBE = (): (() => void) => () => {};

/**
 * Resolves a live domain entity, re-resolving on every committed changeset
 * but re-rendering the consumer ONLY when the resolved reference actually
 * changes.
 *
 * The selection atoms hold ids, and the entities they point at live in the
 * patient aggregate, which is mutated in place — so a removal changes no
 * Recoil reference. Subscribing to {@link useDomainVersion} would fix the
 * reactivity but re-render every consumer on every unrelated changeset (an
 * ROI rename, a dose-scale tick). Instead this subscribes to the store and
 * returns the resolved entity as the snapshot: `useSyncExternalStore` keeps
 * the previous value when the new snapshot is `Object.is`-equal, so an
 * unrelated changeset that leaves the resolved entity untouched triggers no
 * re-render, while an add/remove/swap of the selected entity does.
 *
 * `resolve` MUST return a stable reference for unchanged state (a `find()`
 * over the long-lived aggregate entities does). Returning a fresh object
 * each call would defeat the bail-out and loop.
 */
export function useStoreResolvedEntity<T>(
    resolve: () => T | undefined
): T | undefined {
    const session = useSessionOrNull();
    const store = session?.context.store;
    return useSyncExternalStore(store?.subscribe ?? NOOP_SUBSCRIBE, resolve);
}
